import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { AREAS_INTERESSE, WHATSAPP_LINK } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    nome: "",
    empresa: "",
    whatsapp: "",
    email: "",
    cidade: "",
    area_interesse: "",
    mensagem: "",
    consentimento: false,
    website: "",
};

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30";

const Field = ({ label, error, testid, children }) => (
    <label className="block">
        <span className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {label}
        </span>
        {children}
        {error && (
            <span data-testid={testid} className="mt-1.5 block text-xs font-medium text-red-600">
                {error}
            </span>
        )}
    </label>
);

const LeadForm = ({ origem = "site-contato" }) => {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const validate = () => {
        const e = {};
        if (form.nome.trim().length < 3) e.nome = "Informe seu nome completo.";
        if (form.empresa.trim().length < 2) e.empresa = "Informe o nome da empresa.";
        if (form.whatsapp.replace(/\D/g, "").length < 10) e.whatsapp = "Informe um WhatsApp válido, com DDD.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Informe um e-mail válido.";
        if (form.cidade.trim().length < 2) e.cidade = "Informe sua cidade.";
        if (!form.area_interesse) e.area_interesse = "Selecione a área de interesse.";
        if (!form.consentimento) e.consentimento = "É necessário autorizar o uso dos dados.";
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/leads`, { ...form, origem });
            if (data.status === "duplicate") {
                toast.info("Já recebemos um envio recente com estes dados. Nossa equipe entrará em contato.");
            } else {
                toast.success("Dados enviados com sucesso. Em breve entraremos em contato.");
            }
            setDone(true);
        } catch (err) {
            toast.error("Não foi possível enviar agora. Tente novamente ou chame no WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div data-testid="lead-form-success" className="flex h-full flex-col items-start justify-center rounded-3xl border border-emerald-200 bg-emerald-50 p-10">
                <CheckCircle2 size={44} className="text-emerald-600" />
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">Recebemos seus dados.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                    Obrigado pelo contato. Nossa equipe vai analisar as informações e retornar para
                    entender o cenário da sua empresa. Se preferir, acelere a conversa pelo WhatsApp.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="lead-form-success-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                    >
                        Chamar no WhatsApp
                    </a>
                    <button
                        onClick={() => { setDone(false); setForm(initial); }}
                        data-testid="lead-form-send-another"
                        className="rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-white"
                    >
                        Enviar outra mensagem
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="lead-form" className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_24px_60px_-32px_rgba(10,25,47,0.25)] md:p-10" noValidate>
            <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Nome completo *" error={errors.nome} testid="lead-form-error-nome">
                    <input
                        type="text"
                        value={form.nome}
                        onChange={(e) => set("nome", e.target.value)}
                        placeholder="Seu nome"
                        data-testid="lead-form-name-input"
                        className={inputCls}
                        autoComplete="name"
                    />
                </Field>
                <Field label="Nome da empresa *" error={errors.empresa} testid="lead-form-error-empresa">
                    <input
                        type="text"
                        value={form.empresa}
                        onChange={(e) => set("empresa", e.target.value)}
                        placeholder="Sua empresa"
                        data-testid="lead-form-company-input"
                        className={inputCls}
                        autoComplete="organization"
                    />
                </Field>
                <Field label="WhatsApp *" error={errors.whatsapp} testid="lead-form-error-whatsapp">
                    <input
                        type="tel"
                        value={form.whatsapp}
                        onChange={(e) => set("whatsapp", e.target.value)}
                        placeholder="(24) 99999-0000"
                        data-testid="lead-form-phone-input"
                        className={inputCls}
                        autoComplete="tel"
                    />
                </Field>
                <Field label="E-mail *" error={errors.email} testid="lead-form-error-email">
                    <input
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="voce@empresa.com.br"
                        data-testid="lead-form-email-input"
                        className={inputCls}
                        autoComplete="email"
                    />
                </Field>
                <Field label="Cidade *" error={errors.cidade} testid="lead-form-error-cidade">
                    <input
                        type="text"
                        value={form.cidade}
                        onChange={(e) => set("cidade", e.target.value)}
                        placeholder="Barra Mansa, Volta Redonda..."
                        data-testid="lead-form-city-input"
                        className={inputCls}
                        autoComplete="address-level2"
                    />
                </Field>
                <Field label="Área de interesse *" error={errors.area_interesse} testid="lead-form-error-area">
                    <select
                        value={form.area_interesse}
                        onChange={(e) => set("area_interesse", e.target.value)}
                        data-testid="lead-form-pillar-select"
                        className={`${inputCls} ${form.area_interesse ? "" : "text-slate-400"}`}
                    >
                        <option value="" disabled>Selecione</option>
                        {AREAS_INTERESSE.map((area) => (
                            <option key={area} value={area} className="text-slate-900">{area}</option>
                        ))}
                    </select>
                </Field>
            </div>
            <div className="mt-6">
                <Field label="Mensagem" error={errors.mensagem} testid="lead-form-error-mensagem">
                    <textarea
                        value={form.mensagem}
                        onChange={(e) => set("mensagem", e.target.value)}
                        placeholder="Conte brevemente o momento da sua empresa e o que você busca."
                        rows={4}
                        data-testid="lead-form-message-input"
                        className={`${inputCls} resize-none`}
                    />
                </Field>
            </div>
            <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
            />
            <div className="mt-6">
                <label className="flex cursor-pointer items-start gap-3">
                    <input
                        type="checkbox"
                        checked={form.consentimento}
                        onChange={(e) => set("consentimento", e.target.checked)}
                        data-testid="lead-form-consent-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-emerald-600"
                    />
                    <span className="text-xs leading-relaxed text-slate-500">
                        Autorizo a Vorantis Soluções a utilizar os dados informados exclusivamente para
                        responder ao meu contato, conforme a LGPD (Lei nº 13.709/2018). *
                    </span>
                </label>
                {errors.consentimento && (
                    <span data-testid="lead-form-error-consentimento" className="mt-1.5 block text-xs font-medium text-red-600">
                        {errors.consentimento}
                    </span>
                )}
            </div>
            <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-corp px-8 py-4 font-semibold text-white transition-colors duration-200 hover:bg-corp-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
                {loading ? (
                    <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Enviando...
                    </>
                ) : (
                    <>
                        Enviar solicitação
                        <Send size={16} />
                    </>
                )}
            </button>
        </form>
    );
};

export default LeadForm;
